<script setup lang="ts">
import { onMounted, ref, computed, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { Grid } from '@progress/kendo-vue-grid'
import type { GridSortChangeEvent, GridPageChangeEvent } from '@progress/kendo-vue-grid'
import type { SortDescriptor } from '@progress/kendo-data-query'
import CCAddContact from './CCAddContact.vue'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../../logger.ts'
import Swal from 'sweetalert2'

import type { GridContact } from '../../types/AddressBookTypes'
import { getAddressBook, deleteAddressBookEntry } from '../../api/AddressBook'
import { handleError } from '../../services/ErrorService.ts'
import { GetBeeNumberAuthenicatedUser, GetAddressBookEditUrl } from '../../stores/SettingsStore'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

// Localization
const { t } = useI18n()

// Logging setup
const logger = useLogger()
setStoreLogger(logger)
logger.debug('ContactList.vue loaded')


// State
const contacts = ref<GridContact[]>([])
const gridKey = ref(0)
const beeNumberAuth = ref<number>(0)
const selectedField = 'selected' // must match field name used by Kendo for row selection
// Track open menus by addressBookId instead of email to ensure consistency during filtering
const openMenus = ref<Record<number, boolean>>({})
const emailTargets = ref<string[]>([])
const deleteTargets = ref<number[]>([])
const emailModalVisible = ref(false)
const emailTarget = ref<string | null>(null)
const emailSubject = ref('')
const emailBody = ref('')

// Sort state
const sort = ref<Array<SortDescriptor>>([{ field: '', dir: undefined }])

// Paging state
const skip = ref(0)
const take = ref(25) // Default page size

// Filter state
const showFilters = ref(false)
const selectedClassId = ref('')
const selectedGroup = ref('')
const searchQuery = ref('')
interface SearchTag {
  value: string
  field?: string
  displayValue: string
}

const rawSearchQuery = ref('')
const activeSearchTags = ref<SearchTag[]>([])
let searchDebounceTimeout: ReturnType<typeof setTimeout> | null = null

const distinctClassIds = computed(() => {
  const classIds = contacts.value
    .map(contact => contact.classId)
    .filter((value): value is string => !!value) // Remove nulls/undefined
  return Array.from(new Set(classIds)).sort()
})

const distinctGroups = computed(() => {
  const groups = contacts.value
    .map(contact => contact.group)
    .filter((value): value is string => !!value) // Remove nulls/undefined
  return Array.from(new Set(groups)).sort()
})

const hasClassIdTag = computed(() => {
  return activeSearchTags.value.some(tag => tag.field === 'classId')
})

const hasGroupTag = computed(() => {
  return activeSearchTags.value.some(tag => tag.field === 'group')
})

// Fields that can be searched with specific criteria
const searchableFields = [
  { field: 'classId', display: t('consultantCorner.ClassId') },
  { field: 'group', display: t('consultantCorner.contacts.group') }
]
const contactEditUrl = ref('')
const editContact = ref<GridContact>({} as GridContact)
const showSuccessAlert = ref(false)
const successAlertType = ref<'add' | 'edit'>('add')
const lastModifiedContact = ref<GridContact | null>(null)
const confirmDeleteEmail = ref<string | null>(null)
const confirmDeleteVisible = ref(false)
const addContactModalVisible = ref(false)
const selectedItems = ref<GridContact[]>([])
const newContact = ref<GridContact>({
  firstName: '',
  lastName: '',
  emailAddress: '',
  preferredName: '',
  addressBookId: 0,
  primaryPhone: {
    number: '',
    formattedNumber: '',
    countryCode: '',
    codeIdPhoneType: '',
    extension: '',
  },
  group: '',
  selected: false,
  shippingAddress: {
    countryCode: '',         // or default to 'DE' if your app does that
    addressLine1: '',
    addressLine2: '',
    stateCode: '',
    postalCode: '',
    city: '',
    houseNumber: null,
    firstName: null,
    lastName: null,
    addressId: null,
  },
  billingAddress: {
    countryCode: '',         // or default to 'DE' if your app does that
    addressLine1: '',
    addressLine2: '',
    stateCode: '',
    postalCode: '',
    city: '',
    houseNumber: null,
    firstName: null,
    lastName: null,
    addressId: null,
  },
  altPhone1: {
    number: '',
    formattedNumber: '',
    countryCode: '',
    extension: '',
    codeIdPhoneType: ''
  },
  altPhone2: {
    number: '',
    formattedNumber: '',
    countryCode: '',
    extension: '',
    codeIdPhoneType: ''
  },
  classId: '',
})

//computed properties
const someSelected = computed(() =>
  contacts.value.some((c: GridContact) => c[selectedField]) && !allSelected.value
)
const allSelected = computed(() =>
  contacts.value.length > 0 &&
  contacts.value.every((c: GridContact) => c[selectedField])
)

const loader = ref<boolean>(false)
const isMobile = ref(false)

// Check if mobile on mount and window resize
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}
const filteredContacts = computed(() => {
  let result = contacts.value.filter((c: GridContact) => {
    // Apply class filter
    if (selectedClassId.value && c.classId !== selectedClassId.value) {
      return false
    }

    // Apply group filter
    if (selectedGroup.value && c.group !== selectedGroup.value) {
      return false
    }

    // Apply search query
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.trim().toLowerCase()
      return (
        c.firstName?.toLowerCase().includes(query) ||
        c.lastName?.toLowerCase().includes(query) ||
        c.emailAddress?.toLowerCase().includes(query) ||
        c.primaryPhone?.number?.toLowerCase().includes(query) ||
        c.primaryPhone?.formattedNumber?.toLowerCase().includes(query) ||
        c.group?.toLowerCase().includes(query) ||
        c.classId?.toLowerCase().includes(query)
      )
    }

    return true
  })

  // Apply sorting
  if (sort.value[0].field && sort.value[0].dir) {
    const field = sort.value[0].field
    const dir = sort.value[0].dir
    result = [...result].sort((a, b) => {
      const aVal = a[field as keyof GridContact] || ''
      const bVal = b[field as keyof GridContact] || ''
      return dir === 'asc' ?
        aVal.toString().localeCompare(bVal.toString()) :
        bVal.toString().localeCompare(aVal.toString())
    })
  }

  return result
})

// Paginated data for the grid
const paginatedContacts = computed(() => {
  return filteredContacts.value.slice(skip.value, skip.value + take.value)
})

// Total count for pagination info
const totalContacts = computed(() => filteredContacts.value.length)

//columns
const columns = [
  {
    field: selectedField,
    title: '',
    width: 50,
    headerCell: () =>
      h('label', { class: 'k-checkbox-label' }, [
        h('input', {
          type: 'checkbox',
          class: 'k-checkbox',
          checked: allSelected.value,
          indeterminate: someSelected.value,
          onClick: toggleSelectAll
        })
      ])
  },
  {
    field: 'firstName',
    title: t('consultantCorner.contacts.firstName'),
    width: resolveColumnWidth('150px', t('consultantCorner.contacts.firstName')),
    sortable: true
  },
  {
    field: 'lastName',
    title: t('consultantCorner.contacts.lastName'),
    width: resolveColumnWidth('150px', t('consultantCorner.contacts.lastName')),
    sortable: true
  }
  ,
  {
    field: 'emailAddress',
    title: t('consultantCorner.contacts.email'),
    width: resolveColumnWidth('200px', t('consultantCorner.contacts.email')),
  }
  ,
  {
    title: t('consultantCorner.contacts.phoneNumber'),
    width: resolveColumnWidth('140px', t('consultantCorner.contacts.phoneNumber')),
    field: 'emailAddress',// Use emailAddress to find phone number, something to do with Kendo Grid's cell rendering...
    cell: (h: any, tdProps: any) => {
      const contact = contacts.value.find(c => c.emailAddress === tdProps?.children?.[0])
      const phone = contact?.primaryPhone?.formattedNumber || contact?.primaryPhone?.number || ''
      console.log('Rendering phone number:', phone)
    console.log('contact', contact)
      return h('td', {}, phone)
    }
  },
  {
    field: 'classId',
    title: t('consultantCorner.ClassId'),
    width: resolveColumnWidth('120px', t('consultantCorner.ClassId'))
  },
  {
    field: 'group',
    title: t('consultantCorner.contacts.group'),
    width: resolveColumnWidth('120px', t('consultantCorner.contacts.group')),
  },
  {
    title: t('consultantCorner.contacts.actions'),
    field: 'emailAddress', //use emailAddress to find actions,something to do with Kendo Grid's cell rendering...
    width: resolveColumnWidth('80px', t('consultantCorner.contacts.actions')),
    headerCell: () => h('span', {}, t('consultantCorner.contacts.actions')),
    cell: (h: any, tdProps: any) => {
      const email = tdProps?.children?.[0]

      // Determine if the menu should open upward for bottom rows
      const pageRowCount = paginatedContacts.value.length
      const rowIndex = paginatedContacts.value.findIndex((c: any) => c.emailAddress === email)
      const openUpward = pageRowCount > 3 && rowIndex >= pageRowCount - 3

      return h('div', { style: { position: 'relative' },class:'k-action' }, [
        h('button', {
          class: 'k-button',
          onClick: () => {
            // Close all other menus first
            const wasOpen = openMenus.value[email]
            openMenus.value = {}
            // Toggle this menu
            openMenus.value[email] = !wasOpen
          }
        }, '⋮'),

        openMenus.value[email] &&
        h('ul', {
          class: 'k-menu k-menu-group contact-grid-dropdown',
          style: {
            position: 'absolute',
            ...(openUpward
              ? { bottom: '100%', top: 'auto' }
              : { top: '100%' }),
            left: '-25px',
            background: 'white',
            border: '1px solid #cccccc',
            padding: '4px',
            margin: '0',
            listStyle: 'none',
            minWidth: '120px'
          }
        }, [
          h('li', {
            class: 'k-menu-item',
            onClick: () => {
              console.log('clicked')
              sendEmailAllSelected(email)
              // Close all menus
              openMenus.value = {}
            }
          }, t('consultantCorner.contacts.sendEmail')),
          // Only show edit option if no other rows are selected
          selectedItems.value.length <= 1 && (() => {
            const contact = contacts.value.find(c => c.emailAddress === email)
            const isConsultantClass = contact?.classId?.toLowerCase().includes('consultant')

            return h('li', {
              class: isConsultantClass ? 'k-menu-item k-menu-item-disabled' : 'k-menu-item',
              style: isConsultantClass ? {
                opacity: '0.5',
                cursor: 'not-allowed',
                pointerEvents: 'none'
              } : {},
              onClick: async () => {
                if (isConsultantClass) {
                  return
                }
                const addressBookId = contact?.addressBookId
                if (addressBookId) {
                  // Close all menus before navigating
                  openMenus.value = {}
                  window.location.href = contactEditUrl.value + '?contact=' + addressBookId
                }
              }
            }, t('consultantCorner.contacts.edit'))
          })(),
          h('li', {
            class: 'k-menu-item',
            onClick: () => {
              logger.info(`Delete ${email}`)
              const contact = contacts.value.find(c => c.emailAddress === email)
              const addressBookId = contact?.addressBookId
              if (!addressBookId) {
                logger.warn('No addressBookId found for this row')
                return
              }
              deleteAllSelected(addressBookId,email)
              // Close all menus
              openMenus.value = {}
            }
            }, t('consultantCorner.contacts.delete'))
        ])
      ])
    }
  }
]

onMounted(async () => {
  logger.info('onMounted hook triggered')

  // Set up mobile detection
  checkMobile()
  window.addEventListener('resize', checkMobile)

  contactEditUrl.value = await GetAddressBookEditUrl()

  try {
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    logger.debug('Retrieved beeNumber:', beeNumber)

    beeNumberAuth.value = beeNumber !== null ? beeNumber : 0
    logger.info('Calling getAddressBook with beeNumber:', beeNumberAuth.value)
    loader.value = true
    const response = await getAddressBook()
    loader.value = false

    contacts.value = response.items.map(item => {
      const mappedContact = {
        ...item,
        selected: false,
        actions: ''
      };
      return mappedContact;
    })
    logger.debug('First contact:', contacts.value[0])
    logger.info(`Loaded ${contacts.value.length} contacts`)
  } catch (error: unknown) {
    logger.error('Error loading contacts:', error)
  }
})

function openAddContactModal() {
  addContactModalVisible.value = true
}
function applyExampleTag(field: string, value: string) {
  // Add the tag directly with the field information
  activeSearchTags.value.push({
    field,
    value,
    displayValue: value
  })
}

function onSearchInput(event: Event) {
  const value = (event.target as HTMLInputElement).value
  searchQuery.value = value
  // Reset to first page when search changes
  skip.value = 0
}

function onSortChange(event: GridSortChangeEvent) {
  sort.value = event.sort
}

function onPageChange(event: GridPageChangeEvent) {
  skip.value = event.page.skip
  take.value = event.page.take
}

function clearFilters() {
  selectedClassId.value = ''
  selectedGroup.value = ''
  searchQuery.value = ''
  // Reset to first page when filters change
  skip.value = 0
}

function applyFilters() {
  showFilters.value = false
}

async function submitNewContact(contact: GridContact) {
  // Contact has already been created by CCAddContact component
  // Just handle the success response
  handleAddContactSuccess(contact)
}

const handleAddContactSuccess = async (response: any) => {
  if (response) {
    const newContact = {
      ...response,
      selected: false
    }
    contacts.value.push(newContact)
    lastModifiedContact.value = newContact
    successAlertType.value = 'add'
    showSuccessAlert.value = true
    addContactModalVisible.value = false
  }
}

function showContactSuccessAlert(contact: GridContact) {
  Swal.fire({
    title: t('consultantCorner.contacts.addSuccess'),
    text: `${contact.firstName} ${contact.lastName} ${t('consultantCorner.contacts.addedSuccessfully')}`,
    icon: 'success',
    confirmButtonText: t('buttons.Okay'),
    confirmButtonColor: '#1a6e7d'
  })
}

const onSelectionChange = (event: { dataItem: GridContact }) => {
  const { dataItem } = event

  // Toggle selection state
  dataItem[selectedField] = !dataItem[selectedField]

  // Update selectedItems
  selectedItems.value = contacts.value.filter((item: GridContact) => item[selectedField])
  logger.debug('Selected contacts:', selectedItems.value)
}

const handleEmailModalClose = (c: GridContact) => {
  emailModalVisible.value = false
}

const handleMenuClose = (c: GridContact) => {
  if (c.addressBookId) {
    openMenus.value[c.addressBookId] = false
    openMenus.value = { ...openMenus.value }
  }
}

const handleEmailCancel = (c: GridContact) => {
  emailModalVisible.value = false
}

const handleDeleteModalClose = (c: GridContact) => {
  confirmDeleteVisible.value = false
}

const handleEmailModalSubmit = (c: GridContact) => {
  logger.info('Sending email to:', emailTarget.value)
  logger.info('Subject:', emailSubject.value)
  logger.info('Body:', emailBody.value)

  // Example: await api.sendEmail(emailTarget.value, emailSubject.value, emailBody.value)

  emailModalVisible.value = false
  emailSubject.value = ''
  emailBody.value = ''
}

const onSearchSubmit = (event: Event) => {
  console.log('onSearchSubmit', event)
}

const handleMenuItemClick = (c: GridContact) => {
  console.log('handleDeleteClick', c)
  const addressBookId = c.addressBookId
  if (!addressBookId) {
    logger.warn('No addressBookId found for this row')
    return
  }
  deleteAllSelected(addressBookId, c.emailAddress)
}

function toggleSelectAll(event: Event) {
  const checked = !(allSelected.value) // manually invert

  contacts.value.forEach((c: GridContact) => {
    c[selectedField] = checked
  })

  contacts.value = [...contacts.value] // trigger reactivity
  selectedItems.value = checked ? [...contacts.value] : []
  logger.debug('Toggled all selected via header checkbox:', selectedItems.value)
}
function sendEmailAllSelected(email: string  ) {
  const selected = selectedItems.value
  if (selected.length === 0) {
    window.open(`mailto:${email}`, '_blank')
  } else{
  emailTargets.value = selected.map(c => c.emailAddress)
  const emailstring = emailTargets.value.join(', ')

    window.open(`mailto:${emailstring}`, '_blank')
  }

}
function deleteAllSelectedold( addressBookId: number,email: string) {
 // if (!confirm(`Are you sure you want to delete ${email}?`)) return;
 confirmDeleteEmail.value = email
  confirmDeleteVisible.value = true
  const selected = selectedItems.value
  if (selected.length === 0) {
    deleteAddressBookEntry(addressBookId, beeNumberAuth.value)
    .then(() => {
      logger.info(`Deleted contact with email: ${email}`)
      contacts.value = contacts.value.filter((c: GridContact) => c.emailAddress !== email)
    })
    .catch(error => {
      logger.error('Error deleting contact:', error)
    })
  }else {
    deleteTargets.value = selected.map(c => c.addressBookId)
    logger.info('Deleting contacts with IDs:', deleteTargets.value)

    deleteTargets.value.forEach(id => {
      deleteAddressBookEntry(id, beeNumberAuth.value)
        .then(() => {
          logger.info(`Deleted contact with ID: ${id}`)
          contacts.value = contacts.value.filter(c => c.addressBookId !== id)
        })
        .catch(error => {
          logger.error('Error deleting contact:', error)
        })
    })
  }
}
function deleteAllSelected(addressBookId: number, email: string) {

  if(selectedItems.value.length === 0 ) {
    confirmDeleteEmail.value = email
  } else {
    confirmDeleteEmail.value = selectedItems.value.map(c => c.emailAddress).join(', ')
  }

  confirmDeleteVisible.value = true

  // Store the exact contact to delete
  const contact = contacts.value.find(c => c.emailAddress === email)
  const addressBookIdToDelete = contact?.addressBookId ?? addressBookId

  // Store the single delete target (if no items selected)
  deleteTargets.value = selectedItems.value.length === 0
    ? [addressBookIdToDelete]
    : selectedItems.value.map(c => c.addressBookId)

}
function confirmDelete() {
  if (deleteTargets.value.length === 0) {
    logger.warn('No items to delete')
    confirmDeleteVisible.value = false
    return
  }

  deleteTargets.value.forEach(id => {
    deleteAddressBookEntry(id, beeNumberAuth.value)
      .then(() => {
        logger.info(`Deleted contact with ID: ${id}`)
        contacts.value = contacts.value.filter(c => c.addressBookId !== id)
      })
      .catch(async error => {
        logger.error('Error deleting contact:', error)
        await handleError(error, t)
      })
  })

  confirmDeleteVisible.value = false
  confirmDeleteEmail.value = null
}

function submitEmail() {
  logger.info('Sending email to:', emailTarget.value)
  logger.info('Subject:', emailSubject.value)
  logger.info('Body:', emailBody.value)

  // Example: await api.sendEmail(emailTarget.value, emailSubject.value, emailBody.value)

  emailModalVisible.value = false
  emailSubject.value = ''
  emailBody.value = ''
}

// Action handlers for the grid buttons
function editContactAction(contactId: number) {
  if (!contactId) return
  logger.info(`Editing contact with ID: ${contactId}`)
  window.location.href = `${contactEditUrl.value}?contact=${contactId}`
}

function deleteContactAction(contactId: number, email: string) {
  if (!contactId) {
    logger.warn('No addressBookId found for this row')
    return
  }
  logger.info(`Deleting contact with ID: ${contactId}, email: ${email}`)
  deleteAllSelected(contactId, email)
}



</script>

<template>
  <div>
    <div class="filter-flyout" :class="{ 'filter-flyout-open': showFilters }">
            <div class="filter-flyout-header">
               <div class="filter-flyout-subheader">
              <h4>{{ t('consultantCorner.contacts.filters') }}</h4>
                            <div>{{ t('consultantCorner.contacts.filterSub') }}</div>

                            </div>

              <button class="k-button k-flat" @click="showFilters = false">
                <span class="k-icon k-i-close">x</span>
              </button>
            </div>


            <div class="filter-flyout-content">

              <!-- Class Filter -->
              <div class="filter-group">
                <label>{{ t('consultantCorner.ClassId') }}</label>
                <select v-model="selectedClassId" class="k-dropdown">
                  <option value="">{{ t('consultantCorner.contacts.allClasses') }}</option>
                  <option v-for="classId in distinctClassIds" :key="classId" :value="classId">
                    {{ classId }}
                  </option>
                </select>
              </div>

              <!-- Group Filter -->
              <div class="filter-group">
                <label>{{ t('consultantCorner.contacts.group') }}</label>
                <select v-model="selectedGroup" class="k-dropdown">
                  <option value="">{{ t('consultantCorner.contacts.allGroups') }}</option>
                  <option v-for="group in distinctGroups" :key="group" :value="group">
                    {{ group }}
                  </option>
                </select>
              </div>
            </div>

            <div class="filter-flyout-footer d-none">
              <button class="k-button" @click="clearFilters">{{ t('consultantCorner.contacts.clearFilters') }}</button>
              <button class="k-button k-primary" @click="applyFilters">{{ t('consultantCorner.contacts.applyFilters') }}</button>
            </div>
          </div>
    <div v-if="emailModalVisible">
      <div class="modal-overlay">
        <div class="modal">
          <h3>{{ t('consultantCorner.contacts.sendEmail') }}</h3>
          <p>{{ t('consultantCorner.contacts.emailTo', { email: emailTarget }) }}</p>
          <input v-model="emailSubject" :placeholder="t('consultantCorner.contacts.emailSubject')" class="k-textbox" />
          <textarea v-model="emailBody" :placeholder="t('consultantCorner.contacts.emailMessage')" class="k-textbox" rows="5"></textarea>
          <div style="margin-top: 1rem;">
            <button class="k-button" @click="emailModalVisible = false">{{ t('consultantCorner.contacts.cancel') }}</button>
            <button class="k-button k-primary" @click="submitEmail">{{ t('consultantCorner.contacts.send') }}</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="confirmDeleteVisible" class="deletemodal modal-overlay" @click.self="confirmDeleteVisible = false">
      <div class="modal">
        <p>{{ t('consultantCorner.contacts.deleteConfirm', { email: confirmDeleteEmail }) }}</p>
        <div class="k-dialog-buttongroup">
          <button class="k-button m-1" @click="confirmDeleteVisible = false">{{ t('consultantCorner.contacts.cancel') }}</button>
          <button class="k-button m-1 k-primary" @click="confirmDelete">{{ t('consultantCorner.contacts.delete') }}</button>
        </div>
      </div>
    </div>

    <!-- <div v-if="showSuccessAlert" class="alert alert-success alert-dismissible fade show" role="alert">
      {{ t(`consultantCorner.contacts.${successAlertType}Success`) }}
      <a href="#" @click.prevent="viewModifiedContact" class="alert-link">{{ t('consultantCorner.contacts.viewContact') }}</a>
      <button type="button" class="btn-close" @click="showSuccessAlert = false"></button>
    </div> -->

    <h4>{{ t('consultantCorner.contacts.title') }}</h4>
    <div class="k-grid k-grid-md k-grid-scrollable">
      <div class="p-3">
        <div class="d-block d-lg-flex justify-content-between">
          <div class="d-flex">
            <span class="bold header">{{ t('consultantCorner.contacts.allContacts') }}</span>
            <div class="contact-pill">{{ t('consultantCorner.contacts.contactCount', { count: contacts.length }) }}</div>
          </div>
          <button class="k-button k-primary" @click="openAddContactModal">
            + {{ t('consultantCorner.contacts.newContact') }}
          </button>
        </div>
        <div>{{ t('consultantCorner.contacts.manageContacts') }}</div>
      </div>

      <div style="margin-bottom: 1rem;" class="k-toolbar d-flex justify-content-end align-items-center">
        <div class="filter-button-container" style="position: relative;">
          <button class="k-button" @click="showFilters = !showFilters">
            <span class="k-icon k-i-filter"></span>
            {{ t('consultantCorner.contacts.filters') }}
          </button>

          <!-- Filter Flyout -->

        </div>

        <div class="search-container">
          <input
            v-model="searchQuery"
            @input="onSearchInput"
            class="k-textbox"
            :placeholder="t('consultantCorner.contacts.searchPlaceholder')"
            style="width: 300px;"
          />
        </div>
      </div>



      <!-- Desktop Grid View -->
      <Grid
        v-if="!isMobile"
        ref="grid"
        class="contact-grid"
        :data-items="paginatedContacts"
        :sort="sort"
        :resizable="true"
        :sortable="true"
        :pageable="{
          buttonCount: 5,
          info: true,
          type: 'numeric',
          pageSizes: [10, 25, 50, 100],
          previousNext: true
        }"
        :skip="skip"
        :take="take"
        :total="totalContacts"
        :loader="loader"
        :columns="columns"
        :key="gridKey"
        :data-item-key="'addressBookId'"
        :selectable="{ enabled: true, checkboxOnly: true, mode: 'multiple' }"
        :selected-field="selectedField"
        @selectionchange="onSelectionChange"
        @sortchange="onSortChange"
        @pagechange="onPageChange"
      />

      <!-- Mobile Card View -->
      <div v-else class="contact-cards-container">
        <div v-if="loader" class="text-center p-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">{{$t('labels.Loading')}}</span>
          </div>
        </div>
        <div v-else-if="filteredContacts.length === 0" class="text-center p-4 text-muted">
          {{ t('consultantCorner.contacts.noContactsFound') }}
        </div>
        <div v-else class="contact-cards">
          <div
            v-for="contact in filteredContacts"
            :key="contact.addressBookId"
            class="contact-card"
            :class="{ 'contact-card-selected': contact.selected }"
          >
            <!-- Card Header with Checkbox and Menu -->
            <div class="contact-card-header">
              <label class="contact-card-checkbox">
                <input
                  type="checkbox"
                  class="k-checkbox"
                  :checked="contact.selected"
                  @change="onSelectionChange({ dataItem: contact })"
                />
              </label>
              <div class="contact-card-menu">
                <button
                  class="k-button k-button-flat"
                  @click="openMenus[contact.addressBookId] = !openMenus[contact.addressBookId]"
                >
                  ⋮
                </button>
                <div
                  v-if="openMenus[contact.addressBookId]"
                  class="contact-card-dropdown"
                >
                  <button
                    class="dropdown-item"
                    @click="sendEmailAllSelected(contact.emailAddress); openMenus[contact.addressBookId] = false"
                  >
                    {{ t('consultantCorner.contacts.sendEmail') }}
                  </button>
                  <button
                    v-if="selectedItems.length <= 1 && !contact.classId?.toLowerCase().includes('consultant')"
                    class="dropdown-item"
                    @click="editContactAction(contact.addressBookId); openMenus[contact.addressBookId] = false"
                  >
                    {{ t('buttons.Edit') }}
                  </button>
                  <button
                    class="dropdown-item text-danger"
                    @click="deleteContactAction(contact.addressBookId, contact.emailAddress); openMenus[contact.addressBookId] = false"
                  >
                    {{ t('consultantCorner.contacts.delete') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Card Body -->
            <div class="contact-card-body">
              <div class="contact-card-name">
                {{ contact.firstName }} {{ contact.lastName }}
              </div>
              <div class="contact-card-info">
                <div class="contact-card-info-item">
                  <span class="contact-card-label">{{ t('columns.emailAddress') }}:</span>
                  <a :href="`mailto:${contact.emailAddress}`" class="contact-card-value">
                    {{ contact.emailAddress }}
                  </a>
                </div>
                <div v-if="contact.primaryPhone?.formattedNumber || contact.primaryPhone?.number" class="contact-card-info-item">
                  <span class="contact-card-label">{{ t('columns.phone') }}:</span>
                  <a :href="`tel:${contact.primaryPhone?.number}`" class="contact-card-value">
                    {{ contact.primaryPhone?.formattedNumber || contact.primaryPhone?.number }}
                  </a>
                </div>
                <div v-if="contact.classId" class="contact-card-info-item">
                  <span class="contact-card-label">{{ t('consultantCorner.ClassId') }}:</span>
                  <span class="contact-card-value">{{ contact.classId }}</span>
                </div>
                <div v-if="contact.group" class="contact-card-info-item">
                  <span class="contact-card-label">{{ t('consultantCorner.contacts.group') }}:</span>
                  <span class="contact-card-value">{{ contact.group }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CCAddContact
      v-if="addContactModalVisible"
      :modelValue="newContact"
      :isEdit="false"
      @cancel="addContactModalVisible = false"
      @submit="submitNewContact"
      @success="showContactSuccessAlert"
    />
  </div>
</template>

<style lang="scss" scoped>
.deletemodal .modal {
max-height: 300px;
}
.deletemodal button.k-button.m-1.k-primary {
  border: 1px solid black;
}
.deletemodal .modal:before{display:none;}

:deep(.k-checkbox:checked) {
  border-color: var(--vt-c-teal-medium);
  background-color: var(--vt-c-teal-medium);
}

:deep(.k-checkbox:checked:focus) {
  box-shadow: 0 0 0 2px rgba(var(--vt-c-teal-medium), 0.2);
}

.search-container {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.search-tags {
  display: flex;
  flex-wrap: wrap;
  gap: $gap-sm;
  margin-bottom: 8px;
}

.search-tag {
  display: inline-flex;
  align-items: center;
  gap: $spacing-1;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-sm;
  background-color: #e9ecef;
}

.search-help {
  @include typography-small;
  color: #6c757d;
  margin-top: -8px;
}

.example-tags {
  display: flex;
  gap: $gap-sm;
  margin-bottom: 8px;
}

.example-tag {
  cursor: pointer;
  opacity: 0.65;
  transition: all 0.2s ease;
}

.example-tag[class*='class-'] {
  background-color: #e3f2fd;
  border: 2px dotted #90caf9;
  color: #1976d2;
}

.example-tag[class*='group-'] {
  background-color: #f3e5f5;
  border: 2px dotted #ce93d8;
  color: #7b1fa2;
}

.example-tag:hover {
  opacity: 1;
  border-style: solid;
}

.example-tag[class*='class-']:hover {
  background-color: #bbdefb;
}

.filter-button-container {
  position: relative;
}

.filter-flyout {
  position: absolute;
  right: 0;
  top: 0;
  width: 400px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  z-index: $z-notification;
  display: none;
  height:100vh;
}


.filter-flyout-header {
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-flyout-content {
  padding: 1rem;
}

.filter-flyout-footer {
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.filter-group {
  margin-bottom: 1rem;
}

.filter-group label {
  @include font-weight-medium;
  display: block;
  margin-bottom: 0.5rem;
}

.filter-group select {
  width: 100%;
}

.example-tag[class*='group-']:hover {
  background-color: #e1bee7;
}

.example-tag .k-icon {
  @include typography-caption;
  margin-left: 4px;
}
</style>

<style lang="scss" scoped>

.action-buttons {
  display: flex;
  gap: $spacing-1;
  justify-content: center;
}

.action-buttons .k-button {
  @include typography-caption;
  padding: $spacing-1 $spacing-2;
  white-space: nowrap;
  min-width: 50px;
}
</style>
<style lang="scss">
  input[type="checkbox"].k-checkbox {
    padding: $spacing-2-5;
  }
  section.content:has(.filter-flyout-open) {

    position: unset;
  }
  .filter-flyout-open{display:block !important;}
  .k-button.k-primary {
    background-color: var(--vt-c-teal-medium);
    color: white;
  }
  .k-toolbar{
    border-color:var(--kendo-color-border, black);
  }
  input.k-textbox {
    padding: $spacing-1;
    margin: $spacing-2-5;
  }
  .bold.header{align-self: center;
    @include typography-h4;
    @include font-weight-bold;
    margin-right: 10px;
  }
  .contact-pill{
    background-color: #e0f2f5;
    border-radius: $radius-xl;
    padding-left: 7px;
    padding-right: 7px;
    padding-top: 2px;
    padding-bottom: 2px;
    color: #23616c;
    align-self: center;
  }
  .modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(black, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-loading-overlay;
}
.modal-overlay .modal{display: flex;
  flex-direction: column;

  position: relative;
}
.modal {
  background: white;
  padding: 2rem;
  border-radius: $radius-md;
  width: 800px;

  .k-textbox,
  .k-dropdown,
  select {
    @include typography-body;
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #cccccc;
    border-radius: $radius-md;
    background: var(--vt-c-white);
    box-sizing: border-box;
  }

  .k-textbox:focus,
  .k-dropdown:focus,
  select:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(#0d6efd, 0.2);
  }
}
.k-grid{border-radius:$radius-md;}

.k-table .k-button{border: none;
    margin: 0 auto;
    width: 99%;}

    .k-grid-header th {border:none !important;}
    .k-table-tbody td {border-left:none !important;border-right:none !important;}
    .k-table-alt-row {
    background: unset !important;
    background-color:unset !important;
  }
  .k-table-row td ,.k-actions{
    border-bottom: .5px solid rgba(#e1d4d4, 0.25) !important;
  }


.contact-grid .k-grid-content {
    padding-bottom: 0;
}

/* Mobile Card View Styles */
.contact-cards-container {
  padding: 1rem;
}

.contact-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(black, 0.1);
  transition: all 0.2s ease;
}

.contact-card:hover {
  box-shadow: 0 2px 8px rgba(black, 0.2);
}

.contact-card-selected {
  border-color: var(--vt-c-teal-medium);
  background-color: #e9f5f7;
}

.contact-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.contact-card-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.contact-card-checkbox .k-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.contact-card-menu {
  position: relative;
}

.contact-card-menu .k-button {
  @include typography-h4;
  padding: 0.25rem 0.5rem;
  line-height: 1;
  min-width: auto;
}

.contact-grid-dropdown {
  z-index: $z-notification;
}

.contact-card-dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  box-shadow: 0 2px 8px rgba(black, 0.2);
  z-index: $z-notification;
  min-width: 150px;
  margin-top: 0.25rem;
}

.contact-card-dropdown .dropdown-item {
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.contact-card-dropdown .dropdown-item:hover {
  background-color: var(--vt-c-white-soft);
}

.contact-card-dropdown .dropdown-item.text-danger {
  color: #dc3545;
}

.contact-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-card-name {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #212529;
  margin-bottom: 0.5rem;
}

.contact-card-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-card-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.contact-card-label {
  @include typography-caption;
  @include font-weight-medium;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.contact-card-value {
  @include typography-small;
  color: #212529;
  word-break: break-word;
}

.contact-card-value[href] {
  color: var(--vt-c-teal-medium);
  text-decoration: none;
}

.contact-card-value[href]:hover {
  text-decoration: underline;
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .k-toolbar {
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.1rem;
  }

  .search-container {
    width: 100%;
  }
  .k-button.k-primary{padding:10px;}

  .search-container .k-textbox {
    width: 100% !important;
  }

  .filter-flyout {
    width: 100% !important;
    left: 0;
    right: 0;
  }

}

  </style>
